import React, { useRef, useEffect, useState } from 'react';
import AnalysisChart from '../../../common/Charts/AnalysisChart';
import LoaderOverlay from '../../../common/Loader/LoaderOverlay';
import { useLocation, useNavigate } from 'react-router-dom';
import '../../../../../src/AssetStatusForm.scss'
import { faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Papa from 'papaparse';
import { saveAs } from 'file-saver';

type AuxProperty = {
    aux1: {
        label: string;
        index: string;
        value: string;
        checked: boolean;
    };
    aux2: {
        label: string;
        index: string;
        value: string;
        checked: boolean;
    };
    aux3: {
        label: string;
        index: string;
        value: string;
        checked: boolean;
    };
    mode: {
        label: string;
        index: string;
        value: string;
        checked: boolean;
    };
    unit: {
        label: string;
        index: string;
        value: string;
        checked: boolean;
    };
}

// Define types
type AuxPropertyValue = {
    label: string;
    index: string;
    value: string;
    checked: boolean;
};

interface AuxField {
    label: string;
    index: string;
    value: string;
    checked: boolean;
    unit: string | null;
}

interface TrendsSelection {
    username: string;
    tubingPressure: boolean;
    casingPressure: boolean;
    linePressure: boolean;
    gasRate: boolean;
    state: boolean;
    batteryVoltage: boolean;
    solarVoltage: boolean;
    adHoc: boolean;
    autoRefresh: boolean;
    aux1: AuxField;
    aux2: AuxField;
    aux3: AuxField;
    mode: AuxField;
}


interface AssetAnalysisProps {
    customerId: string,
    assetId: string,
    userTimeZone: string,
    assetName: string,
    handleLogout: () => void,
    startTime?: string,
    currentTab?: number,
    endTime?: string,
    auxProperties: AuxProperty,
    isIntermittentMode?: boolean,
    isBasicWellMethod?: boolean
}

type SelectedValueType = {
    startDateTime: string;
    endDateTime: string;
    assetId: string;
    customerId: string;
    tubingPressure: boolean;
    casingPressure: boolean;
    linePressure: boolean;
    gasRate: boolean;
    state: boolean;
    batteryVoltage: boolean;
    solarVoltage: boolean;
    adHoc: boolean;
    timeZoneId: string;
    aux1: AuxPropertyValue;
    aux2: AuxPropertyValue;
    aux3: AuxPropertyValue;
    mode: AuxPropertyValue;
    unit: AuxPropertyValue;
    [key: string]: string | boolean | Date | {};
};

const getAdjustedTime = (value: number, unit: 'minutes' | 'hours' | 'days') => {
    const currentTime = new Date();

    switch (unit.toLocaleLowerCase()) {
        case 'minutes':
            currentTime.setTime(currentTime.getTime() - value * 60 * 1000);
            break;
        case 'hours':
            currentTime.setTime(currentTime.getTime() - value * 60 * 60 * 1000);
            break;
        case 'days':
            currentTime.setTime(currentTime.getTime() - value * 24 * 60 * 60 * 1000);
            break;
        default:
            throw new Error('Invalid time unit');
    }

    return currentTime;
};

const initalSelectedValue: SelectedValueType = {
    startDateTime: "-4h",
    endDateTime: "0",
    assetId: "",
    customerId: "",
    tubingPressure: true,
    casingPressure: true,
    linePressure: false,
    gasRate: false,
    state: true,
    batteryVoltage: false,
    solarVoltage: false,
    adHoc: false,
    timeZoneId: "",
    autoRefresh: false,
    aux1: { label: 'Disabled', index: '0', value: '0', checked: false },
    aux2: { label: 'Disabled', index: '0', value: '0', checked: false },
    aux3: { label: 'Disabled', index: '0', value: '0', checked: false },
    mode: { label: 'Disabled', index: '0', value: '0', checked: false },
    unit: { label: 'Disabled', index: '0', value: 'imperial', checked: false }
}

export const AssetAnalysisTablet: React.FC<AssetAnalysisProps> = ({ assetName, assetId, customerId, userTimeZone, handleLogout, startTime, currentTab, endTime, auxProperties, isIntermittentMode, isBasicWellMethod }) => {
    const [tabDivHeight, setTabDivHeight] = useState("1050px");
    const [timeInterval, setTimeInterval] = useState<string>(currentTab ? "0m" : "-4h");
    const [selectedValue, setSelectedValue] = useState(currentTab ? { ...initalSelectedValue, startDateTime: formatLocalDate(startTime), endDateTime: formatLocalDate(endTime) } : { ...initalSelectedValue, ...auxProperties });
    const [loading, setLoading] = useState(true);
    const [baseTimeInterval, setBaseTimeInterval] = useState("second")
    const [trendsData, setTrendsData] = useState([])
    const [toggle, setToggle] = useState(false)
    const location = useLocation();
    const navigate = useNavigate();
    const [hasInitialized, setHasInitialized] = useState(false);
    const [isCheckBoxChecked, setIsCheckBoxChecked] = useState(false)
    const isFirstRender = useRef(true);
    let baseTimeIntervalForRange = "second"
    const [oldAuxProperty, setOldAuxProperty] = useState({})
    const [startTimeFromRuns, setStartTimeFromRuns] = useState("");
    const [autoRefresh, setAutoRefresh] = useState(false)
    const [isExpandedChart, setisExpandedChart] = useState(true);
    const [isAnalysisButtonDisabled, setIsAnalysisButtonDisabled] = useState(false)
    const [isUnitChanged, setUnitChanged] = useState(false)

    const baseUrl = process.env.REACT_APP_SERVER_ENDPOINT;
    const apiUrl = `${baseUrl}/Wells/GetTrendData`;

    const userPrefrenceApiUrl = `${baseUrl}/UserPreference/UpdateTrendPreference`;
    const updateTrendsPreference = async () => {

        try {
            const url = `${userPrefrenceApiUrl}`;
            const queryParams = {
                // Add your query parameters here
                startTime: selectedValue.startDateTime,
                endTime: selectedValue.endDateTime,
                assetId: assetId,
                customerId: customerId,
                tubingPressure: selectedValue?.tubingPressure,
                casingPressure: selectedValue?.casingPressure,
                linePressure: selectedValue?.linePressure,
                gasRate: selectedValue?.gasRate,
                state: selectedValue?.state,
                batteryVoltage: selectedValue?.batteryVoltage,
                solarVoltage: selectedValue?.solarVoltage,
                adHoc: selectedValue?.adHoc,
                timeZoneId: userTimeZone,
                autoRefresh: false,
                aux1: selectedValue?.aux1,
                aux2: selectedValue?.aux2,
                aux3: selectedValue?.aux3,
                mode: selectedValue?.mode
            };

            const response = await fetch(url, {
                method: "PUT", // or "GET" depending on your API contract
                headers: {
                    "Content-Type": "application/json",
                    'Authorization': `Bearer ${localStorage.getItem("jwtAccessToken")}`
                },
                body: JSON.stringify(queryParams),
            });

            if (!response.ok) {
                throw new Error(`API responded with ${response.status}`);
            }


        } catch (err) {
            console.error("Failed to update page size preference:", err);
        }
    };

    if (selectedValue?.endDateTime?.length > 1) {
        var timeSpan = (new Date(selectedValue?.endDateTime).getTime() - new Date(selectedValue?.startDateTime).getTime()) / 1000;
        if (timeSpan > 14400) baseTimeIntervalForRange = "minute";
        if (timeSpan > 864000) baseTimeIntervalForRange = "hour";

    }

    const toggleExpand = () => {
        setisExpandedChart(!isExpandedChart);
    };

    const handleResetChartSelection = () => {
        if (timeInterval === "0m") {
            setSelectedValue({ ...selectedValue, startDateTime: formatLocalDate(startTime), endDateTime: formatLocalDate(endTime) })
        }
        else {
            setSelectedValue({ ...selectedValue, startDateTime: timeInterval, endDateTime: "0" })
        }
    }

    const handleRefreshChartSelection = () => {
        setIsAnalysisButtonDisabled(true)
        setToggle(!toggle)
    }


    function formatLocalDate(timestamp) {
        const date = new Date(timestamp);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');

        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.000Z`;
    }


    const fetchTrendsData = async () => {
        setIsAnalysisButtonDisabled(true)
        const queryParams = {
            // Add your query parameters here
            startTime: selectedValue.startDateTime,
            endTime: selectedValue.endDateTime,
            assetId: assetId,
            customerId: customerId,
            tubingPressure: selectedValue.tubingPressure,
            casingPressure: selectedValue.casingPressure,
            linePressure: selectedValue.linePressure,
            gasRate: selectedValue.gasRate,
            state: selectedValue.state,
            batteryVoltage: selectedValue.batteryVoltage,
            solarVoltage: selectedValue.solarVoltage,
            adHoc: selectedValue.adHoc,
            timeZoneId: userTimeZone,
            autoRefresh: false,
            aux1: selectedValue.aux1,
            aux2: selectedValue.aux2,
            aux3: selectedValue.aux3,
            mode: selectedValue.mode

        };

        const requestOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem("jwtAccessToken")}`,
            },
            body: JSON.stringify(queryParams),
        };

        setLoading(true);
        try {
            const response = await fetch(apiUrl, requestOptions);

            if (response.status !== 200) {
                setTrendsData([]);
                if (response.status === 401) {
                    handleLogout();
                }
                setIsAnalysisButtonDisabled(false)
            } else {
                const data = await response.json();
                setTrendsData(data);
                setIsAnalysisButtonDisabled(false)
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchTrendsAutoRefreshData = async () => {
        setIsAnalysisButtonDisabled(true)
        const queryParamsRefreshData = {
            // Add your query parameters here
            startTime: selectedValue.startDateTime,
            endTime: selectedValue.endDateTime === "0" ? selectedValue.endDateTime : formatLocalDate(new Date()),
            assetId: assetId,
            customerId: customerId,
            tubingPressure: selectedValue.tubingPressure,
            casingPressure: selectedValue.casingPressure,
            linePressure: selectedValue.linePressure,
            gasRate: selectedValue.gasRate,
            state: selectedValue.state,
            batteryVoltage: selectedValue.batteryVoltage,
            solarVoltage: selectedValue.solarVoltage,
            adHoc: selectedValue.adHoc,
            timeZoneId: userTimeZone,
            autoRefresh: false,
            aux1: selectedValue.aux1,
            aux2: selectedValue.aux2,
            aux3: selectedValue.aux3,
            mode: selectedValue.mode
        };
        const requestOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem("jwtAccessToken")}`,
            },
            body: JSON.stringify(queryParamsRefreshData),
        };

        try {
            const response = await fetch(apiUrl, requestOptions);

            if (response.status !== 200) {
                setTrendsData([]);
                if (response.status === 401) {
                    handleLogout();
                }
                setIsAnalysisButtonDisabled(false)
            } else {
                const data = await response.json();
                setTrendsData(data);
                setIsAnalysisButtonDisabled(false)
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    };

    const fetchAndStoreUserPreferences = async (
        url: string,
        headers: HeadersInit,
        storageKey: string
    ): Promise<TrendsSelection> => {
        try {

            const userPrefrenceApiResponse = await fetch(url, { headers });
            const data = await userPrefrenceApiResponse.json();
            localStorage.setItem(storageKey, JSON.stringify(data?.userPreference?.trendsSelection as TrendsSelection));
            return data;
        } catch (error) {
            console.error("Error fetching user preferences:", error);
            throw error;
        }
    };

    const scrollToBottom = () => {
        window.scrollTo({
            top: document.documentElement.scrollHeight,
            behavior: "smooth",
        });
    };

    const updateCheckedValuesByIndex = (oldProps, newProps) => {
        const mergedProps = { ...newProps };

        Object.keys(mergedProps).forEach(newKey => {

            const newIndex = mergedProps[newKey].index;

            // Find a matching old property by index
            const oldKey = Object.keys(oldProps).find(oldKey => oldProps[oldKey].index === newIndex && oldKey === newKey);

            if (oldKey) {
                // Update `checked` from oldProps if the index matches
                mergedProps[newKey].checked = oldProps[oldKey].checked;
            }
        });

        return mergedProps;
    };

    useEffect(() => {
        const userPrefrenceApiUrl = `${baseUrl}/UserPreference/GetUserPreference`;
        const headers = {
            "Content-Type": "application/json",
            'Authorization': `Bearer ${localStorage.getItem("jwtAccessToken")}`,
        };
        const storageKey = "trendsSelection";

        (async () => {
            try {
                await fetchAndStoreUserPreferences(userPrefrenceApiUrl, headers, storageKey);

            } catch {
            }
        })();
    }, [assetId]);

    useEffect(() => {
        // Function to check if there is any change in the index values
        const raw = localStorage.getItem("trendsSelection");
        const parsed = JSON.parse(raw) as Partial<TrendsSelection>;
        const hasIndexChanged = (oldProps, newProps) => {
            return Object.keys(newProps).some(key =>
                newProps[key].index !== oldProps[key]?.index
            );
        };
        const hasUnitValueChanged = (oldProps, newProps) => {
            return newProps.unit?.value !== oldProps.unit?.value;
        };

        const hasUnitChangedOnly = !hasIndexChanged(oldAuxProperty, auxProperties) && hasUnitValueChanged(oldAuxProperty, auxProperties)
        setUnitChanged(hasUnitChangedOnly)
        // Check if any value in auxProperties is not equal to '0'
        const hasNonZeroValue = Object.values(auxProperties).some(prop => prop.index !== '0' && prop.index !== '');

        if (!hasInitialized && hasNonZeroValue) {
            if (hasIndexChanged(oldAuxProperty, auxProperties) || hasUnitValueChanged(oldAuxProperty, auxProperties)) {
                // Initialize only if auxProperties contains values and the index values have changed                
                setSelectedValue({
                    ...initalSelectedValue,
                    tubingPressure: parsed?.tubingPressure,
                    casingPressure: parsed?.casingPressure,
                    linePressure: parsed?.linePressure,
                    gasRate: parsed?.gasRate,
                    batteryVoltage: parsed?.batteryVoltage,
                    solarVoltage: parsed?.solarVoltage,
                    adHoc: parsed?.adHoc,
                    aux1: {
                        ...auxProperties?.aux1,
                        checked: parsed?.aux1?.checked
                    },
                    aux2: {
                        ...auxProperties?.aux2,
                        checked: parsed?.aux2?.checked
                    },
                    aux3: {
                        ...auxProperties?.aux3,
                        checked: parsed?.aux3?.checked
                    },
                    mode: {
                        ...auxProperties?.mode,
                        checked: parsed?.mode?.checked
                    },
                    unit: {
                        ...auxProperties?.unit
                    },
                    assetId: assetId,
                    customerId: customerId,
                    timeZoneId: userTimeZone
                });
                setOldAuxProperty(auxProperties);
                setHasInitialized(true);
            }
        }
        else if (hasInitialized) {
            if (hasIndexChanged(oldAuxProperty, auxProperties) || hasUnitValueChanged(oldAuxProperty, auxProperties)) {
                // Filter oldAuxProperty to retain keys also in auxProperties and merge with auxProperties
                const updatedAuxProperties = updateCheckedValuesByIndex(oldAuxProperty, auxProperties);
                setSelectedValue({
                    ...selectedValue,
                    tubingPressure: parsed?.tubingPressure,
                    casingPressure: parsed?.casingPressure,
                    linePressure: parsed?.linePressure,
                    gasRate: parsed?.gasRate,
                    batteryVoltage: parsed?.batteryVoltage,
                    solarVoltage: parsed?.solarVoltage,
                    adHoc: parsed?.adHoc,
                    aux1: {
                        ...updatedAuxProperties?.aux1,
                        checked: parsed?.aux1?.checked
                    },
                    aux2: {
                        ...updatedAuxProperties?.aux2,
                        checked: parsed?.aux2?.checked
                    },
                    aux3: {
                        ...updatedAuxProperties?.aux3,
                        checked: parsed?.aux3?.checked
                    },
                    mode: {
                        ...updatedAuxProperties?.mode,
                        checked: parsed?.mode?.checked
                    },
                    unit: {
                        ...updatedAuxProperties?.unit
                    }

                });
                setOldAuxProperty(updatedAuxProperties);
            }
        }
    }, [auxProperties, hasInitialized, assetId]);

    useEffect(() => {
        const raw = localStorage.getItem("trendsSelection");
        const parsed = JSON.parse(raw) as Partial<TrendsSelection>;
        setTimeInterval("-4h")
        setStartTimeFromRuns("")
        setSelectedValue({
            ...initalSelectedValue,
            tubingPressure: parsed?.tubingPressure,
            casingPressure: parsed?.casingPressure,
            linePressure: parsed?.linePressure,
            gasRate: parsed?.gasRate,
            batteryVoltage: parsed?.batteryVoltage,
            solarVoltage: parsed?.solarVoltage,
            adHoc: parsed?.adHoc,
            aux1: {
                ...auxProperties?.aux1,
                checked: parsed?.aux1?.checked
            },
            aux2: {
                ...auxProperties?.aux2,
                checked: parsed?.aux2?.checked
            },
            aux3: {
                ...auxProperties?.aux3,
                checked: parsed?.aux3?.checked
            },
            mode: {
                ...auxProperties?.mode,
                checked: parsed?.mode?.checked
            },
            unit: { ...auxProperties?.unit },
            assetId: assetId,
            customerId: customerId,
            timeZoneId: userTimeZone
        })
    }, [assetId, customerId])

    useEffect(() => {
        if (startTime !== "" && startTime !== undefined) {
            setStartTimeFromRuns(startTime)
        }
    }, [startTime])

    useEffect(() => {

        if (startTime !== "" && startTime !== undefined && startTimeFromRuns !== "") {
            setTimeInterval("0m")
            scrollToBottom();
            setSelectedValue({ ...selectedValue, startDateTime: formatLocalDate(startTime), endDateTime: formatLocalDate(endTime), assetId: assetId, customerId: customerId, timeZoneId: userTimeZone })
        } else {
            setSelectedValue({ ...selectedValue, assetId: assetId, customerId: customerId, timeZoneId: userTimeZone })
        }
    }, [startTimeFromRuns])


    useEffect(() => {

        if (!autoRefresh && !isUnitChanged) {
            fetchTrendsData();
        }
    }, [assetId, JSON.stringify(selectedValue), toggle]);

    useEffect(() => {
        if (autoRefresh) {
            const intervalId = setInterval(fetchTrendsAutoRefreshData, 60000);
            return () => clearInterval(intervalId);
        }
    }, [assetId, JSON.stringify(selectedValue), autoRefresh]);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        (async () => {
            try {
                await updateTrendsPreference(); // your API/function call
            } catch (error) {
                // optional error handling
            }
        })();
    }, [isCheckBoxChecked]);


    const handleBaseInterval = (timeInterval: any) => {

        let baseTimeInterval = ""

        switch (timeInterval) {

            case '-1h':
                baseTimeInterval = 'second';
                break;
            case '-4h':
                baseTimeInterval = 'second';
                break;
            case '-12h':
                baseTimeInterval = 'second';
                break;
            case '-24h':
                baseTimeInterval = 'second';
                break;
            case '-24hs':
                baseTimeInterval = 'second';
                break;
            case '-7d':
                baseTimeInterval = 'minute';
                break;
            case '-30d':
                baseTimeInterval = 'hour';
                break;
            case '-180d':
                baseTimeInterval = 'hour';
                break;
            case '-365d':
                baseTimeInterval = 'hour';
                break;
            default:
                baseTimeInterval = 'second'
        }

        return baseTimeInterval
    }


    const handleSelectChange = (event: any) => {
    navigate(`${location.pathname}${location.search}`, { replace: true, state: { assetId, assetName, customerId, startTime, endTime, currentTab: undefined } });
        const selectedTimeInterval = event.target.value
        if (selectedTimeInterval === "0m") {
            setSelectedValue({ ...selectedValue, startDateTime: formatLocalDate(startTime), endDateTime: formatLocalDate(endTime) });
            setTimeInterval(selectedTimeInterval)
            setBaseTimeInterval(handleBaseInterval(selectedTimeInterval))
        }
        else {
            setSelectedValue({ ...selectedValue, startDateTime: selectedTimeInterval, endDateTime: "0" });
            setTimeInterval(selectedTimeInterval)
            setBaseTimeInterval(handleBaseInterval(selectedTimeInterval))
        }
        setAutoRefresh(false)
    };
    // Function to handle checkbox change
    const handleAuxCheckboxChange = (key: string) => {
        setSelectedValue(prevState => {
            // Create a deep copy of the previous state to ensure immutability
            const updatedAux = { ...prevState };

            // Ensure that the key exists in the state and update its checked property
            if (key in updatedAux) {
                updatedAux[key] = {
                    ...updatedAux[key],
                    checked: !updatedAux[key].checked
                };
            }

            return updatedAux;
        });
        setOldAuxProperty(prevOldAux => {
            // Create a deep copy of the oldAuxProperty state to ensure immutability
            const updatedOldAux = { ...prevOldAux };

            // Ensure the key exists in oldAuxProperty and update its checked property
            if (key in updatedOldAux) {
                updatedOldAux[key] = {
                    ...updatedOldAux[key],
                    checked: !updatedOldAux[key].checked
                };
            }

            return updatedOldAux;
        });
        setIsCheckBoxChecked(!isCheckBoxChecked)
        setAutoRefresh(false);
    };

    // Handler function to toggle the checkbox state
    const handleCheckboxChange = (checkboxName: string) => {
        setSelectedValue({
            ...selectedValue,
            [checkboxName]: Boolean(!selectedValue[checkboxName]),
        });
        setIsCheckBoxChecked(!isCheckBoxChecked)
        setAutoRefresh(false)
    };

    const handleAutoRefreshChange = () => {
        setAutoRefresh((prevState) => !prevState);
    };

    function capitalizeLabel(str) {
        return str
            .replace(/([A-Z])/g, ' $1')   // Add space before each capital letter           
            .replace(/^./, (match) => match.toUpperCase()); // Capitalize the first letter
    }

    // Combine data for CSV
    const combineData = (trendsData) => {
        const combined = [];

        trendsData.measurement.forEach((measurement, index) => {
            // Get corresponding aux data or default to empty object
            const aux1Entry = trendsData.aux1[index] || {};
            const aux2Entry = trendsData.aux2[index] || {};
            const aux3Entry = trendsData.aux3[index] || {};
            const aux4Entry = trendsData.mode[index] || {}

            // Convert UTC time to local time string for measurement
            let formattedTime = measurement.Time;
            if (formattedTime) {
                let date = new Date(formattedTime);
                formattedTime = date.toLocaleString('en-US', {
                    hour12: false,
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit'
                });
            }

            // Handle time conversion for aux1, aux2, aux3 if they have time properties
            const formatAuxTime = (aux) => {
                let auxTime = aux.Time;
                if (auxTime) {
                    let date = new Date(auxTime);
                    return date.toLocaleString('en-US', {
                        hour12: false,
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit'
                    });
                }
                return ''; // Return empty string if time is not available
            };

            // Create combined entry with formatted time and handle undefined/null values
            const combinedEntry = {
                Time: formattedTime || '',
                Tubing_Pressure: measurement.Tubing_Pressure !== undefined ? measurement.Tubing_Pressure : '',
                Casing_Pressure: measurement.Casing_Pressure !== undefined ? measurement.Casing_Pressure : '',
                Line_Pressure: measurement.Line_Pressure !== undefined ? measurement.Line_Pressure : '',
                Gas_Rate: measurement.Gas_Rate !== undefined ? measurement.Gas_Rate : '',
                State: measurement.State !== undefined ? measurement.State : '',
                Battery: measurement.Battery !== undefined ? measurement.Battery : '',
                Solar: measurement.Solar !== undefined ? measurement.Solar : '',
                Ad_Hoc: measurement.Ad_Hoc !== undefined ? measurement.Ad_Hoc : ''
            };

            // Conditionally add aux fields if the label is not "Disabled"
            if (selectedValue?.aux1?.label !== 'Disabled') {
                combinedEntry[selectedValue.aux1.label] = aux1Entry.AUX1 !== undefined ? aux1Entry.AUX1 : '';
            }

            if (selectedValue?.aux2?.label !== 'Disabled') {
                combinedEntry[selectedValue.aux2.label] = aux2Entry.AUX2 !== undefined ? aux2Entry.AUX2 : '';
            }

            if (selectedValue?.aux3?.label !== 'Disabled') {
                combinedEntry[selectedValue.aux3.label] = aux3Entry.AUX3 !== undefined ? aux3Entry.AUX3 : '';
            }

            if (selectedValue?.mode?.label !== 'Disabled') {
                combinedEntry[selectedValue.mode.label] = aux4Entry.MODE !== undefined ? aux4Entry.MODE : '';
            }

            combined.push(combinedEntry);
        });

        return combined;
    };


    const downloadCSV = (data, filename = 'data.csv') => {
        const csv = Papa.unparse(data);

        // Create a Blob from the CSV string
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });

        // Use FileSaver.js to save the Blob as a file
        saveAs(blob, filename);

        // Optionally, update any state after the download completes
        setIsAnalysisButtonDisabled(false);
    };

    const handleExport = (trendsData, fileName) => {
        setIsAnalysisButtonDisabled(true)
        const data = combineData(trendsData);
        downloadCSV(data, fileName);
    };

    return (
        <div className="bg-darkcolor-800 content-wrapper pl-[15px] pr-[15px] rounded-lg">
            {/* {loading && !autoRefresh && <LoaderOverlay />} */}
            <div
                onClick={toggleExpand}
                style={{ backgroundColor: "#4A5463", marginBottom: "16px" }}
                className="mx-auto pl-[15px] pr-[15px] flex items-center cursor-pointer p-2"
            >
                <div
                    style={{
                        backgroundColor: "var(--Primary-Indigo-400, #60BFDA)",
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "22px",
                        height: "20px",
                        borderRadius: "10%"
                    }}
                    className="p-2"
                >
                    <FontAwesomeIcon icon={isExpandedChart ? faMinus : faPlus} />
                </div>
                <span
                    style={{ color: "var(--Primary-Indigo-400, #60BFDA)", marginLeft: "5px" }}
                >
                    Analysis
                </span>
            </div>
            {isExpandedChart &&
                <div className="border border-darkcolor-500 pl-[15px] pr-[15px] pb-[15px] mt-[-16px]" style={{ borderTop: 'none', borderRadius: '0 0 8px 8px' }}>
                    <div className="flex bg-darkcolor-800 rounded-lg pt-[25px]">
                        <div className="w-full mx-auto rounded-lg">
                            <div className="mx-auto border border-darkcolor-500 bg-darkcolor-600 rounded-lg">
                                <div className="border border-darkcolor-500 rounded-lg" style={{ height: "60px", width: "100%" }}>
                                    <select id="dropdown" value={timeInterval} onChange={handleSelectChange} className="bg-darkcolor-800" style={{ height: "30px", marginLeft: "10px", marginTop: "10px", width: "100px", paddingLeft: "5px", paddingRight: "5px" }}>
                                        {startTime && <option value="0m">Custom</option>}
                                        <option value="-1m">1 Minute</option>
                                        <option value="-1h">1 Hour</option>
                                        <option value="-4h">4 Hours</option>
                                        <option value="-12h">12 Hours</option>
                                        <option value="-24h">24 Hours</option>
                                        <option value="-24hs">24 Hours(1s)</option>
                                        <option value="-7d">7 Days</option>
                                        <option value="-30d">30 Days</option>
                                        <option value="-180d">180 Days</option>
                                        <option value="-365d">365 Days</option>
                                    </select>
                                    <label className="switch autorefresh-ml">
                                        <input
                                            type="checkbox"
                                            checked={autoRefresh}
                                            onChange={handleAutoRefreshChange}
                                        />
                                        <span className="slider"></span>
                                    </label>
                                    <label style={{ marginLeft: "5px", verticalAlign: "3px" }}> Auto Refresh</label>

                                    <label className="switch" style={{ marginLeft: "5px" }}>
                                        <input
                                            type="checkbox"
                                            checked={selectedValue.state}
                                            onChange={() => handleCheckboxChange('state')}
                                        />
                                        <span className="slider"></span>
                                    </label>
                                    <label style={{ marginLeft: "5px", verticalAlign: "3px" }}> State</label>

                                    <button type="button" disabled={isAnalysisButtonDisabled} className={`btn btn-primary ${isAnalysisButtonDisabled ? 'disabled-button' : ''}`} style={{ marginTop: "5px", marginRight: "2vw", float: "right", backgroundColor: "var(--Primary-Indigo-700, #005F7C)" }} onClick={() => handleExport(trendsData, `${assetName}.csv`)}> Export To CSV</button>
                                    <button type="button" disabled={isAnalysisButtonDisabled} className={`btn btn-primary ${isAnalysisButtonDisabled ? 'disabled-button' : ''}`} style={{ marginTop: "5px", marginLeft: "10px", marginRight: "10px", float: "right", backgroundColor: "var(--Primary-Indigo-700, #005F7C)" }} onClick={handleRefreshChartSelection}> Refresh</button>
                                    <button type="button" disabled={isAnalysisButtonDisabled} className={`btn btn-primary ${isAnalysisButtonDisabled ? 'disabled-button' : ''}`} style={{ marginTop: "5px", marginLeft: "10px", float: "right", backgroundColor: "var(--Primary-Indigo-700, #005F7C)" }} onClick={handleResetChartSelection}> Reset Zoom</button>


                                </div>
                                <div className="border border-darkcolor-500" style={{ height: "125px", width: "100%" }}>
                                    <div style={{ display: "flex", justifyContent: "center", marginTop: "10px" }}>
                                        <button type="button" className="btn btn-primary" style={{
                                            marginLeft: "5px", backgroundColor: "lightblue", color: "black",
                                            backgroundImage: `
                                                repeating-linear-gradient(
                                                    45deg,
                                                    transparent,
                                                    transparent 2px,
                                                    rgba(0,0,0,0.2) 2px,
                                                    rgba(0,0,0,0.2) 4px
                                                ),
                                                repeating-linear-gradient(
                                                    -45deg,
                                                    transparent,
                                                    transparent 2px,
                                                    rgba(0,0,0,0.2) 2px,
                                                    rgba(0,0,0,0.2) 4px
                                                )
                                            ` }}>
                                            Manual/Hold Open
                                        </button>
                                        {isIntermittentMode ? (
                                            <button
                                                type="button"
                                                className="btn btn-primary"
                                                style={{ marginLeft: "5px", backgroundColor: "lightpink", color: "black" }}
                                            >
                                                Close
                                            </button>
                                        ) : (
                                            <>
                                                <button
                                                    type="button"
                                                    className="btn btn-primary"
                                                    style={{ marginLeft: "5px", backgroundColor: "lightpink", color: "black" }}
                                                >
                                                    Close/Fall
                                                </button>

                                                <button
                                                    type="button"
                                                    className="btn btn-primary"
                                                    style={{ marginLeft: "5px", backgroundColor: "#dddd93", color: "black" }}
                                                >
                                                    {isBasicWellMethod ? `Open/Fall` : `Close/POB`}
                                                </button>
                                            </>
                                        )}
                                        <button type="button" className="btn btn-primary" style={{ marginLeft: "5px", backgroundColor: "lightblue", color: "black" }}> Open/Arriving</button>
                                        <button type="button" className="btn btn-primary" style={{ marginLeft: "5px", backgroundColor: "lightgray", color: "black" }}> Open/Slug</button>
                                        <button type="button" className="btn btn-primary" style={{ marginLeft: "5px", backgroundColor: "lightgreen", color: "black" }}> Open/Sales</button>
                                        <button type="button" className="btn btn-primary" style={{
                                            marginLeft: "5px", backgroundColor: "lightpink", color: "black",
                                            backgroundImage: `
                                                repeating-linear-gradient(
                                                    45deg,
                                                    transparent,
                                                    transparent 2px,
                                                    rgba(0,0,0,0.2) 2px,
                                                    rgba(0,0,0,0.2) 4px
                                                ),
                                                repeating-linear-gradient(
                                                    -45deg,
                                                    transparent,
                                                    transparent 2px,
                                                    rgba(0,0,0,0.2) 2px,
                                                    rgba(0,0,0,0.2) 4px
                                                )
                                            ` }}>
                                            Manual/Hold Close
                                        </button>
                                    </div>

                                    <div className="mt-2" style={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: "4px" }}>
                                        {['tubingPressure', 'casingPressure', 'linePressure', 'gasRate', 'batteryVoltage', 'solarVoltage', 'adHoc'].map(key => (
                                            <div key={key} className="flex items-center mr-2">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedValue[key]}
                                                    onChange={() => handleCheckboxChange(key)}
                                                    className="mr-2"
                                                />
                                                <label className="vertical-align-middle">{capitalizeLabel(key)}</label>
                                            </div>
                                        ))}
                                        {Object.entries(selectedValue)
                                            .filter(([key, prop]) =>
                                                (key.startsWith('aux') || key === 'mode') &&
                                                (prop as AuxPropertyValue).index !== '0' &&
                                                (prop as AuxPropertyValue).index !== ''
                                            )
                                            .map(([key, prop], index) => (
                                                <div key={`${key}-${index}`} className="flex items-center mr-2">
                                                    <input
                                                        type="checkbox"
                                                        checked={(prop as AuxPropertyValue).checked}
                                                        onChange={() => handleAuxCheckboxChange(key)}
                                                        className="mr-2"
                                                    />
                                                    <label className="vertical-align-middle">{(prop as AuxPropertyValue).label}</label>
                                                </div>
                                            ))}
                                    </div>

                                </div>

                                <div className="border border-darkcolor-500 rounded-lg" style={{ width: "100%", paddingTop: "10px" }}>
                                    <div style={{ height: "680px", width: "100%" }}>
                                        <AnalysisChart
                                            trendData={trendsData}
                                            selectedValue={selectedValue}
                                            setSelectedValue={setSelectedValue}
                                            setAutoRefresh={setAutoRefresh}
                                            baseTimeInterval={selectedValue.endDateTime !== "0" ? baseTimeIntervalForRange : baseTimeInterval}
                                            isIntermittentMode={isIntermittentMode}
                                        />
                                    </div>
                                    <div
                                        id="legendwrapper"
                                        style={{
                                            marginTop: "150px",
                                            backgroundColor: "var(--darkcolor-600)",
                                            padding: "10px",
                                            borderRadius: "8px",
                                        }}
                                    >
                                        <div id="legenddiv"></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            }
            <div className="mb-[30px]" />
        </div>
    );
};
